import { Box, Grid, styled, Typography } from '@mui/material';
import { useConfiguration } from '@providers/Configuration';
import { getPartCategoryTree } from '@services/productInventory';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DownloadPartsCategories } from './DownloadPartsCategories';
import { PartCategoriesTree } from './PartCategoriesTree';
import { UploadPartsCategoriesForm } from './UploadPartsCategoriesForm';

interface PartsCategoriesSpreadsheetProps {
  accountId: string;
  configurationPID: string;
}

export function PartsCategoriesSpreadsheet({ accountId, configurationPID }: PartsCategoriesSpreadsheetProps) {
  const { parentPID: productPID } = useConfiguration();
  const { t } = useTranslation('manageProduct');

  const {
    data: currentPartCategoriesData,
    isError,
    isLoading,
  } = useQuery(['getPartCategoryTree', configurationPID, productPID], () =>
    getPartCategoryTree({ accountId, configurationPID: configurationPID!, productPID })
  );

  return (
    <Grid container spacing={2} rowSpacing={6} sx={{ py: '6rem' }}>
      <Grid item sm={12} md={6}>
        <StepContainer>
          <StepLabel>1</StepLabel>
          <StepColumn>
            <StepTitle>{t('partsCategories.currentPartsCategories.title')}</StepTitle>
            <Typography>{t('partsCategories.currentPartsCategories.description')}</Typography>
            <DownloadPartsCategories />
            <PartCategoriesTree
              data={currentPartCategoriesData}
              isError={isError}
              isLoading={isLoading}
              titleTranslationKey="manageProduct:partsCategories.currentPartsCategories.treeTitle"
            />
          </StepColumn>
        </StepContainer>
      </Grid>
      <Grid item sm={12} md={6}>
        <StepContainer>
          <StepLabel>2</StepLabel>
          <UploadContainer>
            <StepTitle>{t('partsCategories.uploadNew.title')}</StepTitle>
            <UploadPartsCategoriesForm accountId={accountId} configurationPID={configurationPID!} />
          </UploadContainer>
        </StepContainer>
      </Grid>
    </Grid>
  );
}

const StepContainer = styled(Box)`
  display: flex;
  gap: 1rem;
`;

const StepColumn = styled(Box)`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const UploadContainer = styled('div')`
  flex: 1;
`;

const StepLabel = styled('span')`
  ${({ theme }) => `border: 2px solid ${theme.palette.primary.dark};`}
  height: 3rem;
  width: 3rem;
  min-width: 3rem;
  display: flex;
  align-items: center;
  border-radius: 2rem;
  justify-content: center;
  margin-top: -1rem;
  font-size: large;
`;

const StepTitle = styled('h3')`
  ${({ theme }) => `
    font-size: 1rem;
    font-weight: ${theme.typography.fontWeightBold};
  `}
`;
